import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import ReactDOM from 'react-dom';
import { Box, Typography, IconButton, Drawer, Divider } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { MenuOutlined, CloseOutlined, AccountCircleOutlined, AddOutlined } from '@mui/icons-material';
import { ContentBrowserModal } from './ContentBrowserModal';

const PRIMARY = '#1075BA';
function uid() { return Math.random().toString(36).slice(2, 9); }
const isUrl = str => /^https?:\/\//i.test((str || '').trim()) || /^www\./i.test((str || '').trim());

// ── LogoPopup — appears when logo area is clicked in builder mode ─────────────
function LogoPopup({ anchorEl, config, defaultLogoSrc = '', lobbies = [], onChangeConfig, onClose, activeLobby, onChangeLobby }) {
  const [pos, setPos] = useState(null);
  const [contentBrowserOpen, setContentBrowserOpen] = useState(false);
  const popRef = useRef(null);

  useLayoutEffect(() => {
    if (!anchorEl) return;
    const r = anchorEl.getBoundingClientRect();
    setPos({ top: r.bottom + 8, left: r.left });
  }, [anchorEl]);

  useEffect(() => {
    if (!anchorEl) return;
    const handler = (e) => {
      // Don't close if content browser is open
      if (contentBrowserOpen) return;
      if (popRef.current && !popRef.current.contains(e.target) &&
          anchorEl && !anchorEl.contains(e.target)) {
        onClose();
      }
    };
    const t = setTimeout(() => document.addEventListener('mousedown', handler), 0);
    return () => { clearTimeout(t); document.removeEventListener('mousedown', handler); };
  }, [anchorEl, onClose, contentBrowserOpen]);

  if (!pos) return null;

  const upd = (patch) => onChangeConfig({ ...config, ...patch });

  return ReactDOM.createPortal(
    <>
      <Box
        ref={popRef}
        onMouseDown={e => e.stopPropagation()}
        onClick={e => e.stopPropagation()}
        sx={{
          position: 'fixed', top: pos.top, left: pos.left,
          width: 260, background: '#fff',
          border: '1px solid #e4e9f0', borderRadius: '12px',
          boxShadow: '0 8px 28px rgba(0,0,0,0.14)',
          p: 1.5, zIndex: 99,
          display: 'flex', flexDirection: 'column', gap: 1,
        }}
      >
        {/* Row 1 — Logo image */}
        <Box>
          <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#9ea8b3', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.75 }}>
            Logo Image
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            {config.logoSrc ? (
              <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.5, borderRadius: '7px', background: '#f5f7fa', border: '1px solid #e4e9f0', overflow: 'hidden' }}>
                <Box component="img" src={config.logoSrc} sx={{ height: 20, width: 'auto', objectFit: 'contain', maxWidth: 120 }} />
                <Box onClick={() => upd({ logoSrc: null })} sx={{ ml: 'auto', cursor: 'pointer', color: '#c4cdd8', fontSize: 12, '&:hover': { color: '#dc2626' } }}>✕</Box>
              </Box>
            ) : defaultLogoSrc ? (
              <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.5, borderRadius: '7px', background: '#f5f7fa', border: '1px dashed #d4dbe6', overflow: 'hidden' }}>
                <Box component="img" src={defaultLogoSrc} sx={{ height: 20, width: 'auto', objectFit: 'contain', maxWidth: 120, opacity: 0.6 }} />
                <Box sx={{ ml: 'auto', fontSize: '0.6rem', color: '#b0bac8', whiteSpace: 'nowrap' }}>default</Box>
              </Box>
            ) : (
              <Box
                onClick={() => setContentBrowserOpen(true)}
                sx={{ flex: 1, py: 0.65, borderRadius: '7px', border: '1px dashed #d4dbe6', background: '#f8f9fb', cursor: 'pointer', fontSize: '0.72rem', color: '#9ea8b3', textAlign: 'center', '&:hover': { borderColor: PRIMARY, color: PRIMARY } }}
              >
                Choose image…
              </Box>
            )}
            {(config.logoSrc || defaultLogoSrc) && (
              <Box onClick={() => setContentBrowserOpen(true)} sx={{ px: 0.75, py: 0.5, borderRadius: '6px', border: '1px solid #e4e9f0', cursor: 'pointer', fontSize: '0.65rem', color: '#5a6474', '&:hover': { background: '#f0f4f8' } }}>
                Change
              </Box>
            )}
          </Box>
        </Box>

        <Box sx={{ borderTop: '1px solid #f0f2f5' }} />

        {/* Row 2 — Show Logo toggle */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#3c4555' }}>Show Logo</Typography>
          <Box
            onClick={() => {
              // Prevent both off — if turning off logo, back button must be on
              const next = !(config.showLogo !== false);
              if (!next && !config.showBackButton) return; // can't disable both
              upd({ showLogo: next });
            }}
            sx={{
              width: 36, height: 20, borderRadius: 10, cursor: 'pointer',
              background: config.showLogo !== false ? PRIMARY : '#d4dbe6',
              position: 'relative', transition: 'background 0.2s', flexShrink: 0,
            }}
          >
            <Box sx={{ position: 'absolute', top: 2, left: config.showLogo !== false ? 18 : 2, width: 16, height: 16, borderRadius: '50%', background: '#fff', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
          </Box>
        </Box>

        {/* Row 3 — Show Back Button toggle */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#3c4555' }}>Show Back Button</Typography>
            {/* {config.showBackButton && (
              <Typography sx={{ fontSize: '0.62rem', color: '#9ea8b3', mt: 0.1 }}>
                Shows "← Back to Previous"
              </Typography>
            )} */}
          </Box>
          <Box
            onClick={() => {
              const next = !config.showBackButton;
              if (!next && config.showLogo === false) return;
              upd({ showBackButton: next });
            }}
            sx={{
              width: 36, height: 20, borderRadius: 10, cursor: 'pointer',
              background: config.showBackButton ? PRIMARY : '#d4dbe6',
              position: 'relative', transition: 'background 0.2s', flexShrink: 0, ml: 1,
            }}
          >
            <Box sx={{ position: 'absolute', top: 2, left: config.showBackButton ? 18 : 2, width: 16, height: 16, borderRadius: '50%', background: '#fff', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
          </Box>
        </Box>

      </Box>

      {/* Content browser for logo — separate portal with higher z-index */}
      {contentBrowserOpen && ReactDOM.createPortal(
        <Box sx={{ inset: 0, zIndex: 999999 }}>
          <ContentBrowserModal
            open
            onClose={() => setContentBrowserOpen(false)}
            mediaType="image"
            onSelect={url => { upd({ logoSrc: url }); setContentBrowserOpen(false); }}
          />
        </Box>,
        document.body
      )}
    </>,
    document.body
  );
}

// ── Logo ──────────────────────────────────────────────────────────────────────
function LogoArea({ config, defaultLogoSrc = '', isBuilderMode, onLogoClick, lobbies = [], isBoothLobby = false, onBackToFirstSlide }) {
  const ref = useRef(null);
  const showLogo = isBoothLobby ? false : config.showLogo !== false;
  const showBack = isBoothLobby ? true  : !!config.showBackButton;
  const showBackFirst = !!onBackToFirstSlide; // carousel back-to-first overrides logo

  return (
    <Box
      ref={ref}
      onClick={isBuilderMode ? (e => { e.stopPropagation(); onLogoClick?.(ref.current); }) : undefined}
      sx={{
        display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0,
        ...(isBuilderMode ? {
          cursor: 'pointer', borderRadius: '8px', px: 0.5, py: 0.25,
          outline: '2px solid transparent',
          transition: 'outline 0.12s',
          '&:hover': { outline: `2px solid ${alpha(PRIMARY, 0.4)}` },
        } : {}),
      }}
    >
      {/* Carousel back-to-first button — replaces logo when on non-first slide */}
      {showBackFirst ? (
        <Box
          onClick={e => { e.stopPropagation(); onBackToFirstSlide(); }}
          sx={{
            display: 'flex', alignItems: 'center', gap: '5px',
            px: 1.25, py: 0.6, borderRadius: '20px',
            background: 'rgba(10,14,26,0.72)',
            backdropFilter: 'blur(8px)',
            border: '1px solid rgba(255,255,255,0.18)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.35)',
            cursor: 'pointer', flexShrink: 0,
            transition: 'all 0.15s',
            '&:hover': { background: 'rgba(10,14,26,0.92)', borderColor: 'rgba(255,255,255,0.35)' },
          }}
        >
          <Box sx={{ fontSize: 15, color: '#fff', lineHeight: 1 }}>←</Box>
          <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#fff', lineHeight: 1 }}>Back</Typography>
        </Box>
      ) : (
      <>
      {/* Logo — shown first */}
      {showLogo && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
          {(config.logoSrc || defaultLogoSrc) ? (
            <Box component="img" src={config.logoSrc || defaultLogoSrc} alt={config.logoAlt || 'Logo'}
              sx={{ height: config.logoHeight || 36, width: 'auto', display: 'block', objectFit: 'contain' }} />
          ) : (
            <>
              <Box sx={{ width: 32, height: 32, borderRadius: '50%', background: alpha(PRIMARY, 0.15), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Box sx={{ width: 14, height: 14, borderRadius: '50%', background: PRIMARY }} />
              </Box>
              <Typography sx={{ fontSize: '1rem', fontWeight: 800, color: PRIMARY, letterSpacing: '-0.5px' }}>
                {config.logoText || 'Event'}
              </Typography>
            </>
          )}
        </Box>
      )}

      {/* Back button — shown after logo */}
      {showBack && (
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 0.5,
          px: 1.25, py: 0.5, borderRadius: '8px',
          background: 'rgba(0,0,0,0.06)', border: '1px solid rgba(0,0,0,0.08)',
          cursor: isBuilderMode ? 'pointer' : 'pointer',
          fontSize: '0.78rem', fontWeight: 600, color: PRIMARY,
          transition: 'all 0.15s', '&:hover': { background: 'rgba(0,0,0,0.1)' },
          flexShrink: 0,
        }}>
          <Box sx={{ fontSize: 14, lineHeight: 1 }}>‹</Box>
          Back
        </Box>
      )}

      {/* Builder placeholder when both are hidden — keeps container selectable */}
      {isBuilderMode && !showLogo && !showBack && (
        <Box sx={{ px: 1, py: 0.5, borderRadius: '6px', border: '1px dashed rgba(0,0,0,0.15)', fontSize: '0.68rem', color: 'rgba(0,0,0,0.3)', whiteSpace: 'nowrap' }}>
          Logo area
        </Box>
      )}
      </>
      )}
    </Box>
  );
}

// ── NavLink — live/read-only version ─────────────────────────────────────────
function NavLink({ link, color, mobile = false }) {
  const handleClick = (e) => {
    e.stopPropagation();
    if (link.href) window.open(link.href, link.newTab ? '_blank' : '_self', 'noopener,noreferrer');
  };
  return (
    <Typography onClick={handleClick} sx={{
      fontSize: mobile ? '1rem' : '0.875rem', fontWeight: 500,
      color: color || '#1a1a2e', cursor: 'pointer',
      whiteSpace: mobile ? 'normal' : 'nowrap', py: mobile ? 0.5 : 0,
      transition: 'color 0.15s',
      '&:hover': { color: PRIMARY, textDecoration: 'underline' },
    }}>
      {link.label || 'Link'}
    </Typography>
  );
}

// ── NavLinkPickerPopup — portalled, positioned below anchor ──────────────────
// Shows lobby list + external URL input. Used when clicking a nav link or +.
function NavLinkPickerPopup({ lobbies, currentLobbyId, currentHref, anchorEl, onSelectLobby, onSelectExternal, onClose }) {
  const popRef = useRef(null);
  const [query, setQuery]   = useState('');
  const [pos, setPos]       = useState({ top: 0, left: 0 });

  useLayoutEffect(() => {
    if (!anchorEl) return;
    const r = anchorEl.getBoundingClientRect();
    const popW = 300;
    let left = r.left;
    if (left + popW > window.innerWidth - 8) left = window.innerWidth - popW - 8;
    if (left < 8) left = 8;
    setPos({ top: r.bottom + 6, left });
  }, [anchorEl]);

  useEffect(() => {
    const handler = e => {
      if (popRef.current && !popRef.current.contains(e.target) &&
          anchorEl && !anchorEl.contains(e.target)) onClose();
    };
    const t = setTimeout(() => document.addEventListener('mousedown', handler), 0);
    return () => { clearTimeout(t); document.removeEventListener('mousedown', handler); };
  }, [onClose, anchorEl]);

  const looksLikeUrl = isUrl(query);
  const filtered = lobbies.filter(l => l.name?.toLowerCase().includes(query.toLowerCase()));

  return ReactDOM.createPortal(
    <div
      ref={popRef}
      onMouseDown={e => e.stopPropagation()}
      onClick={e => e.stopPropagation()}
      style={{
        position: 'fixed', top: pos.top, left: pos.left,
        width: 300, maxHeight: 380,
        background: '#fff', border: '1px solid #e4e9f0',
        borderRadius: 12, boxShadow: '0 10px 36px rgba(0,0,0,0.15)',
        display: 'flex', flexDirection: 'column', overflow: 'hidden', zIndex: 99999,
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '9px 12px 7px', borderBottom: '1px solid #f0f4f8', flexShrink: 0 }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: '#1a1a2e' }}>Link destination</span>
        <div onClick={onClose} style={{ width: 20, height: 20, borderRadius: '50%', background: '#f0f4f8', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: 11, color: '#9ea8b3' }}
          onMouseEnter={e => { e.currentTarget.style.background = '#fee2e2'; e.currentTarget.style.color = '#dc2626'; }}
          onMouseLeave={e => { e.currentTarget.style.background = '#f0f4f8'; e.currentTarget.style.color = '#9ea8b3'; }}>✕</div>
      </div>

      {/* Search / URL field */}
      <div style={{ padding: '8px 10px', borderBottom: '1px solid #f0f4f8', flexShrink: 0 }}>
        <div style={{ position: 'relative' }}>
          <input
            autoFocus
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search lobbies or paste a URL…"
            style={{
              width: '100%', boxSizing: 'border-box',
              border: `1.5px solid ${looksLikeUrl ? '#2d9e4e' : '#e4e9f0'}`,
              borderRadius: 7, padding: '6px 10px',
              paddingRight: looksLikeUrl ? 68 : 10,
              fontSize: 12.5, outline: 'none', background: '#f8f9fb', fontFamily: 'inherit',
            }}
          />
          {looksLikeUrl && (
            <div
              onClick={() => { onSelectExternal(query.trim()); onClose(); }}
              style={{ position: 'absolute', right: 5, top: '50%', transform: 'translateY(-50%)', background: '#2d9e4e', color: '#fff', fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 5, cursor: 'pointer' }}
            >Use URL</div>
          )}
        </div>
      </div>

      {/* Lobby list */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '6px 6px' }}>
        {!looksLikeUrl && filtered.length === 0 && (
          <div style={{ textAlign: 'center', padding: '16px 0', fontSize: 12, color: '#9ea8b3' }}>No lobbies found</div>
        )}
        {!looksLikeUrl && filtered.map(lobby => {
          const isSelected = lobby._id === currentLobbyId;
          return (
            <div
              key={lobby._id}
              onClick={() => { onSelectLobby(lobby); onClose(); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '7px 8px',
                borderRadius: 8, cursor: 'pointer', marginBottom: 2,
                background: isSelected ? alpha(PRIMARY, 0.07) : 'transparent',
                border: `1px solid ${isSelected ? PRIMARY : 'transparent'}`,
                transition: 'all 0.12s',
              }}
              onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = '#f5f7fa'; }}
              onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = isSelected ? alpha(PRIMARY, 0.07) : 'transparent'; }}
            >
              <div style={{ width: 36, height: 24, borderRadius: 5, flexShrink: 0, background: lobby.bgUrl ? `url(${lobby.bgUrl}) center/cover` : alpha(PRIMARY, 0.1), border: '1px solid #e4e9f0' }} />
              <div style={{ fontSize: 12.5, fontWeight: isSelected ? 700 : 500, color: isSelected ? PRIMARY : '#2c3444', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {lobby.name}
              </div>
            </div>
          );
        })}
      </div>
    </div>,
    document.body
  );
}

// ── EditableNavLink — builder-mode clickable link with picker ─────────────────
function EditableNavLink({ link, lobbies, linkColor, onChange, onDelete }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [hovered, setHovered]       = useState(false);
  const [editing, setEditing]       = useState(false);
  const [draft, setDraft]           = useState(link.label || '');
  const anchorRef  = useRef(null);
  const inputRef   = useRef(null);

  useEffect(() => { setDraft(link.label || ''); }, [link.label]);
  useEffect(() => { if (editing && inputRef.current) inputRef.current.select(); }, [editing]);

  const commitLabel = () => {
    setEditing(false);
    const trimmed = draft.trim();
    if (trimmed && trimmed !== link.label) onChange({ ...link, label: trimmed });
    else setDraft(link.label || '');
  };

  return (
    <>
      <Box
        ref={anchorRef}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        sx={{
          position: 'relative',
          display: 'flex', alignItems: 'center',
          px: 0.75, py: 0.4,
          borderRadius: '6px',
          background: pickerOpen ? alpha(PRIMARY, 0.08) : hovered ? alpha(PRIMARY, 0.05) : 'transparent',
          border: `1px solid ${pickerOpen ? PRIMARY : hovered ? alpha(PRIMARY, 0.25) : 'transparent'}`,
          transition: 'all 0.15s',
        }}
      >
        {editing ? (
          /* Inline text input for label editing */
          <Box
            component="input"
            ref={inputRef}
            value={draft}
            onChange={e => setDraft(e.target.value)}
            onBlur={commitLabel}
            onKeyDown={e => {
              if (e.key === 'Enter') commitLabel();
              if (e.key === 'Escape') { setDraft(link.label || ''); setEditing(false); }
              e.stopPropagation();
            }}
            onClick={e => e.stopPropagation()}
            onMouseDown={e => e.stopPropagation()}
            sx={{
              border: 'none', outline: 'none', background: 'transparent',
              fontSize: '0.875rem', fontWeight: 500, fontFamily: 'inherit',
              color: linkColor || '#1a1a2e', width: `${Math.max(draft.length, 4)}ch`,
              minWidth: 40, maxWidth: 120, p: 0, cursor: 'text',
            }}
          />
        ) : (
          /* Label — double-click to edit, single-click to open picker */
          <Typography
            onClick={e => { e.stopPropagation(); setPickerOpen(v => !v); }}
            onDoubleClick={e => { e.stopPropagation(); setPickerOpen(false); setEditing(true); }}
            title="Click to change destination · Double-click to rename"
            sx={{
              fontSize: '0.875rem', fontWeight: 500,
              color: linkColor || '#1a1a2e', whiteSpace: 'nowrap',
              cursor: 'pointer', userSelect: 'none',
            }}
          >
            {link.label || 'Link'}
          </Typography>
        )}

        {/* Delete × on hover */}
        {hovered && !editing && (
          <Box
            onClick={e => { e.stopPropagation(); onDelete(); }}
            sx={{
              position: 'absolute', top: -6, right: -6,
              width: 14, height: 14, borderRadius: '50%',
              background: '#dc2626', color: '#fff',
              fontSize: 9, fontWeight: 700, lineHeight: 1,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              zIndex: 5, cursor: 'pointer',
              '&:hover': { background: '#b91c1c' },
            }}
          >✕</Box>
        )}
      </Box>

      {pickerOpen && (
        <NavLinkPickerPopup
          lobbies={lobbies}
          currentLobbyId={link.lobbyId}
          currentHref={link.href}
          anchorEl={anchorRef.current}
          onSelectLobby={lobby => onChange({ ...link, lobbyId: lobby._id, label: link.label || lobby.name, href: '' })}
          onSelectExternal={href => onChange({ ...link, lobbyId: null, href, label: link.label || href })}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </>
  );
}

// ── EditableNavLinksArea — the full center column in builder mode ─────────────
function EditableNavLinksArea({ config, lobbies, onChange }) {
  const [addPickerOpen, setAddPickerOpen] = useState(false);
  const addBtnRef = useRef(null);
  const links = config.links || [];

  const updateLink = (i, patch) => onChange([...links.slice(0, i), patch, ...links.slice(i + 1)]);
  const deleteLink = (i) => onChange(links.filter((_, idx) => idx !== i));
  const addLobbyLink = (lobby) => onChange([...links, { id: uid(), label: lobby.name, lobbyId: lobby._id, href: '' }]);
  const addExternalLink = (href) => onChange([...links, { id: uid(), label: href, lobbyId: null, href }]);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
      {links.length === 0 && (
        <Typography sx={{ fontSize: '0.75rem', color: '#c0c8d4', fontStyle: 'italic', mr: 0.5 }}>
          No nav links
        </Typography>
      )}

      {links.map((link, i) => (
        <EditableNavLink
          key={link.id || i}
          link={link}
          lobbies={lobbies}
          linkColor={config.style?.linkColor}
          onChange={patch => updateLink(i, patch)}
          onDelete={() => deleteLink(i)}
        />
      ))}

      {/* + add button */}
      <Box
        ref={addBtnRef}
        onClick={e => { e.stopPropagation(); setAddPickerOpen(v => !v); }}
        sx={{
          width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          border: `1.5px dashed ${addPickerOpen ? PRIMARY : '#c4cdd8'}`,
          color: addPickerOpen ? PRIMARY : '#c4cdd8',
          cursor: 'pointer', transition: 'all 0.15s',
          '&:hover': { borderColor: PRIMARY, color: PRIMARY, background: alpha(PRIMARY, 0.06) },
        }}
      >
        <AddOutlined sx={{ fontSize: 13 }} />
      </Box>

      {addPickerOpen && (
        <NavLinkPickerPopup
          lobbies={lobbies}
          currentLobbyId={null}
          currentHref=""
          anchorEl={addBtnRef.current}
          onSelectLobby={lobby => { addLobbyLink(lobby); setAddPickerOpen(false); }}
          onSelectExternal={href => { addExternalLink(href); setAddPickerOpen(false); }}
          onClose={() => setAddPickerOpen(false)}
        />
      )}
    </Box>
  );
}

// ── AccountButton ─────────────────────────────────────────────────────────────
function AccountButton({ config, pill = false, participantName }) {
  
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const btnBg    = config.style?.accountBg    || PRIMARY;
  const btnColor = config.style?.accountColor || '#fff';
  const label    = config.accountLabel || (participantName ? participantName : 'My Account');

  useEffect(() => {
    if (!open) return;
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const t = setTimeout(() => document.addEventListener('mousedown', handler), 0);
    return () => { clearTimeout(t); document.removeEventListener('mousedown', handler); };
  }, [open]);

  return (
    <Box ref={ref} sx={{ position: 'relative', flexShrink: 0 }}>
      {/* <Box onClick={e => { e.stopPropagation(); setOpen(v => !v); }} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 1.75, py: pill ? 0.75 : 0.8, borderRadius: pill ? '999px' : '8px', background: btnBg, cursor: 'pointer', transition: 'filter 0.15s', '&:hover': { filter: 'brightness(0.9)' } }}>
        <AccountCircleOutlined sx={{ fontSize: 16, color: btnColor }} />
        <Typography sx={{ fontSize: '0.875rem', fontWeight: 700, color: btnColor, whiteSpace: 'nowrap' }}>{label}</Typography>
        <Box sx={{ fontSize: 9, color: alpha(btnColor, 0.7), lineHeight: 1, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}>▼</Box>
      </Box> */}

      <Box
        onClick={e => { e.stopPropagation(); setOpen(v => !v); }}
        sx={{
          width: 36, height: 36, borderRadius: '50%', cursor: 'pointer',
          background: btnBg, display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: open ? `0 0 0 3px ${alpha(btnBg, 0.3)}` : 'none',
          transition: 'box-shadow 0.15s',
          '&:hover': { filter: 'brightness(0.92)' },
        }}
      >
        <Typography sx={{ fontSize: '0.75rem', fontWeight: 800, color: btnColor, lineHeight: 1, letterSpacing: '0.02em' }}>
          GV
        </Typography>
      </Box>

      {open && (
        <Box sx={{ position: 'absolute', top: 'calc(100% + 6px)', right: 0, background: '#fff', border: '1px solid #e4e9f0', borderRadius: '10px', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', minWidth: 140, py: '4px', zIndex: 9999, overflow: 'hidden' }}>
          <Box onClick={e => { e.stopPropagation(); setOpen(false); }} sx={{ px: 2, py: 1.25, cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600, color: '#dc2626', '&:hover': { background: '#fff5f5' }, transition: 'background 0.12s' }}>
            Logout
          </Box>
        </Box>
      )}
    </Box>
  );
}

// ── Mobile drawer ─────────────────────────────────────────────────────────────
function MobileDrawer({ open, onClose, config, defaultLogoSrc = '', participantName, onBackToFirstSlide }) {
  const links = config.links || [];
  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: 280, display: 'flex', flexDirection: 'column' } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2.5, py: 2, borderBottom: '1px solid #f0f2f5' }}>
        <LogoArea config={config} defaultLogoSrc={defaultLogoSrc} onBackToFirstSlide={onBackToFirstSlide} />
        <IconButton size="small" onClick={onClose} sx={{ color: '#5a6474' }}><CloseOutlined sx={{ fontSize: 20 }} /></IconButton>
      </Box>
      <Box sx={{ flex: 1, overflowY: 'auto', px: 2.5, py: 2, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
        {links.length === 0
          ? <Typography sx={{ fontSize: '0.8rem', color: '#c0c8d4', fontStyle: 'italic' }}>No nav links added</Typography>
          : links.map((link, i) => <NavLink key={link.id || i} link={link} color={config.style?.linkColor} mobile />)
        }
      </Box>
      <Divider />
      <Box sx={{ px: 2.5, py: 2 }}><AccountButton config={config} participantName={participantName} /></Box>
    </Drawer>
  );
}

// ── Mobile bar ────────────────────────────────────────────────────────────────
function MobileBar({ config, defaultLogoSrc = '', pill = false, participantName, onBackToFirstSlide }) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const bg = pill ? (config.style?.pillBg || '#fff') : (config.style?.background || '#fff');
  const bar = (
    <Box sx={{ width: '100%', background: bg, borderBottom: pill ? 'none' : (config.style?.borderBottom || `1px solid ${alpha('#000', 0.08)}`), px: '20px', height: pill ? 56 : 60, display: 'flex', alignItems: 'center', justifyContent: 'space-between', boxSizing: 'border-box', borderRadius: pill ? '999px' : 0, boxShadow: pill ? '0 4px 24px rgba(0,0,0,0.10)' : 'none', border: pill ? `1px solid ${alpha('#000', 0.07)}` : 'none', position: 'relative' }}>
      <LogoArea config={config} defaultLogoSrc={defaultLogoSrc} onBackToFirstSlide={onBackToFirstSlide} />
      <Box onClick={e => { e.stopPropagation(); setDrawerOpen(true); }} sx={{ width: 36, height: 36, borderRadius: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#5a6474', border: '1px solid #e4e9f0', transition: 'all 0.15s', '&:hover': { background: alpha(PRIMARY, 0.06), borderColor: alpha(PRIMARY, 0.3), color: PRIMARY } }}>
        <MenuOutlined sx={{ fontSize: 20 }} />
      </Box>
      <MobileDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} config={config} defaultLogoSrc={defaultLogoSrc} participantName={participantName} onBackToFirstSlide={onBackToFirstSlide} />
    </Box>
  );
  if (!pill) return bar;
  return (
    <Box sx={{ width: '100%', px: '16px', pt: `${config.style?.pillTop ?? 16}px`, pb: '8px', display: 'flex', justifyContent: 'center', boxSizing: 'border-box' }}>{bar}</Box>
  );
}

// ── Classic header ────────────────────────────────────────────────────────────
// ── useNavOverflow ────────────────────────────────────────────────────────────
// Returns true when nav links collectively exceed their container width.
function useNavOverflow(navRef, linkCount) {
  const [overflows, setOverflows] = useState(false);
  useLayoutEffect(() => {
    const el = navRef.current;
    if (!el) return;
    const check = () => setOverflows(el.scrollWidth > el.clientWidth + 2);
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [linkCount]);
  return overflows;
}

// ── Classic header ────────────────────────────────────────────────────────────
function ClassicHeader({ config, defaultLogoSrc = '', participantName, isBuilderMode, lobbies, onChangeLinks, onLogoClick, isBoothLobby, onBackToFirstSlide }) {
  const links     = config.links || [];
  const navRef    = useRef(null);
  const overflows = useNavOverflow(navRef, links.length);
  const [drawerOpen, setDrawerOpen] = useState(false);
  // In builder mode always show links (editing); in preview collapse on overflow
  const showBurger = !isBuilderMode && overflows;

  return (
    <Box sx={{ width: '100%', background: config.style?.background || '#ffffff', borderBottom: config.style?.borderBottom || `1px solid ${alpha('#000', 0.08)}`, px: config.style?.paddingX || '48px', height: config.style?.height || 72, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, boxSizing: 'border-box' }}>
      <LogoArea config={config} defaultLogoSrc={defaultLogoSrc} isBuilderMode={isBuilderMode} onLogoClick={onLogoClick} isBoothLobby={isBoothLobby} onBackToFirstSlide={onBackToFirstSlide} />

      {isBuilderMode && onChangeLinks ? (
        <EditableNavLinksArea config={config} lobbies={lobbies || []} onChange={onChangeLinks} />
      ) : (
        <Box ref={navRef} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1, justifyContent: 'center', overflow: 'hidden', minWidth: 0, visibility: showBurger ? 'hidden' : 'visible' }}>
          {links.length === 0
            ? <Typography sx={{ fontSize: '0.78rem', color: '#c0c8d4', fontStyle: 'italic', whiteSpace: 'nowrap' }}>No nav links added</Typography>
            : links.map((link, i) => <NavLink key={link.id || i} link={link} color={config.style?.linkColor} />)
          }
        </Box>
      )}

      {showBurger ? (
        <>
          <Box onClick={e => { e.stopPropagation(); setDrawerOpen(true); }}
            sx={{ width: 36, height: 36, borderRadius: 1.5, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', border: '1px solid #e4e9f0', color: '#5a6474', transition: 'all 0.15s', '&:hover': { background: alpha(PRIMARY, 0.06), borderColor: alpha(PRIMARY, 0.3), color: PRIMARY } }}>
            <MenuOutlined sx={{ fontSize: 20 }} />
          </Box>
          <MobileDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} config={config} defaultLogoSrc={defaultLogoSrc} participantName={participantName} onBackToFirstSlide={onBackToFirstSlide} />
        </>
      ) : (
        <AccountButton config={config} pill={false} participantName={participantName} />
      )}
    </Box>
  );
}

// ── Pill header ───────────────────────────────────────────────────────────────
function PillHeader({ config, defaultLogoSrc = '', participantName, isBuilderMode, lobbies, onChangeLinks, onLogoClick, isBoothLobby, onBackToFirstSlide }) {
  const links     = config.links || [];
  const navRef    = useRef(null);
  const overflows = useNavOverflow(navRef, links.length);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const paddingX  = config.style?.paddingX || '40px';
  const maxWidth  = config.style?.pillMaxWidth || 1100;
  const topOffset = config.style?.pillTop ?? 16;
  const showBurger = !isBuilderMode && overflows;

  return (
    <Box sx={{ width: '100%', px: paddingX, pt: `${topOffset + 8}px`, pb: `${topOffset + 8}px`, display: 'flex', justifyContent: 'center', boxSizing: 'border-box' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: config.style?.pillBg || '#ffffff', borderRadius: '999px', boxShadow: '0 4px 24px rgba(0,0,0,0.10)', px: 2.5, height: 56, gap: 2, width: '100%', maxWidth, boxSizing: 'border-box', border: `1px solid ${alpha('#000', 0.07)}` }}>
        <LogoArea config={config} defaultLogoSrc={defaultLogoSrc} isBuilderMode={isBuilderMode} onLogoClick={onLogoClick} lobbies={lobbies} isBoothLobby={isBoothLobby} onBackToFirstSlide={onBackToFirstSlide} />

        {isBuilderMode && onChangeLinks ? (
          <EditableNavLinksArea config={config} lobbies={lobbies || []} onChange={onChangeLinks} />
        ) : (
          <Box ref={navRef} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1, justifyContent: 'center', overflow: 'hidden', minWidth: 0, visibility: showBurger ? 'hidden' : 'visible' }}>
            {links.length === 0
              ? <Typography sx={{ fontSize: '0.73rem', color: '#c0c8d4', fontStyle: 'italic', whiteSpace: 'nowrap' }}>No nav links added</Typography>
              : links.map((link, i) => <NavLink key={link.id || i} link={link} color={config.style?.linkColor} />)
            }
          </Box>
        )}

        {showBurger ? (
          <>
            <Box onClick={e => { e.stopPropagation(); setDrawerOpen(true); }}
              sx={{ width: 34, height: 34, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', border: '1px solid #e4e9f0', color: '#5a6474', transition: 'all 0.15s', '&:hover': { background: alpha(PRIMARY, 0.06), borderColor: alpha(PRIMARY, 0.3), color: PRIMARY } }}>
              <MenuOutlined sx={{ fontSize: 20 }} />
            </Box>
            <MobileDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} config={config} defaultLogoSrc={defaultLogoSrc} participantName={participantName} onBackToFirstSlide={onBackToFirstSlide} />
          </>
        ) : (
          <AccountButton config={config} pill participantName={participantName} />
        )}
      </Box>
    </Box>
  );
}

// ── Main export ───────────────────────────────────────────────────────────────
export function LobbyHeader({ config = {}, selected = false, onSelect, onChangeLinks, onChangeConfig, lobbies = [], participantName, isMobile = false, isBoothLobby = false, activeLobby, onChangeLobby, defaultLogoSrc = '', onBackToFirstSlide }) {
  const isPill   = (config.template || 'classic') === 'pill';
  const isSticky = config.sticky === true;
  const isBuilderMode = !!onSelect;

  const [logoAnchor, setLogoAnchor] = useState(null);

  const stickySpacerHeight = isPill
    ? (config.style?.pillTop ?? 16) + 56 + 8
    : (config.style?.height || 72);

  const handleLogoClick = isBuilderMode ? (el) => setLogoAnchor(el) : undefined;

  const inner = isMobile
    ? <MobileBar config={config} defaultLogoSrc={defaultLogoSrc} pill={isPill} participantName={participantName} onBackToFirstSlide={onBackToFirstSlide} />
    : isPill
      ? <PillHeader config={config} defaultLogoSrc={defaultLogoSrc} participantName={participantName} isBuilderMode={isBuilderMode} lobbies={lobbies} onChangeLinks={onChangeLinks} onLogoClick={handleLogoClick} isBoothLobby={isBoothLobby} onBackToFirstSlide={onBackToFirstSlide} />
      : <ClassicHeader config={config} defaultLogoSrc={defaultLogoSrc} participantName={participantName} isBuilderMode={isBuilderMode} lobbies={lobbies} onChangeLinks={onChangeLinks} onLogoClick={handleLogoClick} isBoothLobby={isBoothLobby} onBackToFirstSlide={onBackToFirstSlide} />;

  const selectionSx = onSelect ? {
    outline: selected ? `2px solid ${PRIMARY}` : '2px solid transparent',
    outlineOffset: -2, transition: 'outline-color 0.15s',
    '&:hover': { outline: `2px solid ${selected ? PRIMARY : alpha(PRIMARY, 0.35)}` },
    cursor: 'pointer', userSelect: 'none',
  } : {};

  const handleClick = onSelect ? (e => { e.stopPropagation(); onSelect(); }) : undefined;

  return (
    <>
      {isPill ? (
        <>
          <Box onClick={handleClick} sx={{ position: isSticky ? 'sticky' : 'relative', top: 0, height: 0, overflow: 'visible', mb: `-${stickySpacerHeight}px`, zIndex: isSticky ? 200 : 10 }}>
            <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, ...selectionSx }}>
              {inner}
              {onSelect && (
                <Box sx={{ position: 'absolute', top: 4, left: 4, px: 0.9, py: 0.2, background: selected ? PRIMARY : alpha(PRIMARY, 0.12), borderRadius: '4px 0 4px 0', fontSize: '0.58rem', fontWeight: 700, color: selected ? '#fff' : PRIMARY, letterSpacing: '0.5px', textTransform: 'uppercase', pointerEvents: 'none' }}>
                  Header · Pill
                </Box>
              )}
            </Box>
          </Box>
          <Box sx={{ height: stickySpacerHeight, flexShrink: 0, pointerEvents: 'none' }} />
        </>
      ) : (
        <Box onClick={handleClick} sx={{ position: isSticky ? 'sticky' : 'relative', top: 0, zIndex: isSticky ? 200 : 'auto', ...selectionSx }}>
          {inner}
          {onSelect && (
            <Box sx={{ position: 'absolute', top: 4, left: 4, px: 0.9, py: 0.2, background: selected ? PRIMARY : alpha(PRIMARY, 0.12), borderRadius: '4px 0 4px 0', fontSize: '0.58rem', fontWeight: 700, color: selected ? '#fff' : PRIMARY, letterSpacing: '0.5px', textTransform: 'uppercase', pointerEvents: 'none' }}>
              Header
            </Box>
          )}
        </Box>
      )}

      {/* Logo popup */}
      {isBuilderMode && logoAnchor && onChangeConfig && (
        <LogoPopup
          anchorEl={logoAnchor}
          config={config}
          defaultLogoSrc={defaultLogoSrc}
          lobbies={lobbies}
          onChangeConfig={onChangeConfig}
          onClose={() => setLogoAnchor(null)}
          activeLobby={activeLobby}
          onChangeLobby={onChangeLobby}
        />
      )}
    </>
  );
}

export default LobbyHeader;